import React, { useEffect, useMemo } from 'react';
import { FiCheck } from 'react-icons/fi';
import { GetSessionResponse } from '../../config/api';
import { UserProgress } from '../../types/progress';

interface TodaysProgressProps {
  sessionData: GetSessionResponse | null;
  isLoading: boolean;
  allTracks: UserProgress[];
  expandedTrackId: string | null;
  onCompletionStatusChange?: (allCompleted: boolean) => void;
}

interface ProgressItem {
  text: string;
  subtext?: string;
  completed: boolean;
}

// Daily practice localStorage utility functions
const getTodaysDate = () => {
  const d = new Date(); // local time
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`; // YYYY-MM-DD in local timezone
};

const getDailyPracticeData = (trackId: string) => {
  const today = getTodaysDate();
  const storedDate = localStorage.getItem(`dailyPractice_${trackId}_date`);
  
  // If stored date is not today, do not write here; return empty state for this track.
  if (storedDate !== today) {
    return { learning: [], accuracy: [], fluency: [] };
  }
  
  // Return existing data for this track
  return {
    learning: (() => { try { return JSON.parse(localStorage.getItem(`dailyPractice_${trackId}_learning`) || '[]'); } catch { return []; } })(),
    accuracy: (() => { try { return JSON.parse(localStorage.getItem(`dailyPractice_${trackId}_accuracy`) || '[]'); } catch { return []; } })(),
    fluency: (() => { try { return JSON.parse(localStorage.getItem(`dailyPractice_${trackId}_fluency`) || '[]'); } catch { return []; } })()
  };
};

// Write-only utility to sweep old data and initialize current track
const sweepAndInitDailyPracticeData = (trackId: string) => {
  const today = getTodaysDate();
  try {
    const toClear: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('dailyPractice_') && key.endsWith('_date')) {
        const date = localStorage.getItem(key);
        if (date !== today) toClear.push(key);
      }
    }
    toClear.forEach(dateKey => {
      const id = dateKey.slice('dailyPractice_'.length, -'_date'.length);
      localStorage.removeItem(`dailyPractice_${id}_date`);
      localStorage.removeItem(`dailyPractice_${id}_learning`);
      localStorage.removeItem(`dailyPractice_${id}_accuracy`);
      localStorage.removeItem(`dailyPractice_${id}_fluency`);
    });
    // Ensure current track has today's date
    localStorage.setItem(`dailyPractice_${trackId}_date`, today);
  } catch (e) {
    console.warn('Daily practice sweep/init failed; continuing.', e);
  }
};

const updateDailyPracticeData = (trackId: string, newFactIds: { learning: string[], accuracy: string[], fluency: string[] }) => {
  const current = getDailyPracticeData(trackId);
  
  // Merge new fact IDs with existing ones for the SAME track (to preserve completed facts)
  // Using Set to ensure uniqueness within each category
  const learningSet = new Set([...current.learning, ...newFactIds.learning]);
  const accuracySet = new Set([...current.accuracy, ...newFactIds.accuracy]);
  const fluencySet = new Set([...current.fluency, ...newFactIds.fluency]);
  
  const updatedLearning = Array.from(learningSet);
  const updatedAccuracy = Array.from(accuracySet);
  const updatedFluency = Array.from(fluencySet);
  
  // Save back to localStorage for this track
  try {
    localStorage.setItem(`dailyPractice_${trackId}_learning`, JSON.stringify(updatedLearning));
    localStorage.setItem(`dailyPractice_${trackId}_accuracy`, JSON.stringify(updatedAccuracy));
    localStorage.setItem(`dailyPractice_${trackId}_fluency`, JSON.stringify(updatedFluency));
  } catch (error) {
    console.error('Failed to save daily practice data to localStorage:', error);
  }
  
  return {
    learning: updatedLearning,
    accuracy: updatedAccuracy,
    fluency: updatedFluency
  };
};

const TodaysProgress: React.FC<TodaysProgressProps> = ({ sessionData, isLoading, allTracks, expandedTrackId, onCompletionStatusChange }) => {
  
  // Sweep old localStorage data and initialize current track (before dailyProgress memo is used)
  useEffect(() => {
    if (!isLoading && expandedTrackId) {
      sweepAndInitDailyPracticeData(expandedTrackId);
    }
  }, [isLoading, expandedTrackId]);
  
  // Extract fact IDs from session data and merge with localStorage
  const dailyProgress = useMemo(() => {
    // ALWAYS wait for API to complete first
    if (isLoading || !expandedTrackId) {
      return null;
    }
    
    // API done - use sessionData or localStorage fallback
    if (!sessionData?.availableActivities) {
      return getDailyPracticeData(expandedTrackId);
    }

    const { availableActivities } = sessionData;
    
    // Extract fact IDs by status (filtering like session management service)
    const learningFactIds = availableActivities.learn?.facts
      ?.filter((fact: any) => fact.progressStatus === 'learning' || !fact.progressStatus)
      ?.map((fact: any) => fact.factId) || [];
    
    const accuracyFactIds = availableActivities.accuracyPractice?.facts
      ?.filter((fact: any) => fact.progressStatus === 'accuracyPractice')
      ?.map((fact: any) => fact.factId) || [];
    
    // Extract fluency fact IDs from both groupedFacts and facts
    const fluencyFactIds: string[] = [];
    
    // From groupedFacts
    if (availableActivities.fluencyPractice?.groupedFacts) {
      Object.values(availableActivities.fluencyPractice.groupedFacts).forEach((levelFacts: any) => {
        if (Array.isArray(levelFacts)) {
          levelFacts.forEach((fact: any) => {
            if (fact.progressStatus?.startsWith('fluency')) {
              fluencyFactIds.push(fact.factId);
            }
          });
        }
      });
    }
    
    // From facts array
    if (availableActivities.fluencyPractice?.facts) {
      availableActivities.fluencyPractice.facts.forEach((fact: any) => {
        if (fact.progressStatus?.startsWith('fluency')) {
          fluencyFactIds.push(fact.factId);
        }
      });
    }
    
    // Remove duplicates from fluency facts
    const fluencySet = new Set(fluencyFactIds);
    const uniqueFluencyFactIds = Array.from(fluencySet);
    
    // Merge with any existing for this track (do not write here)
    const current = getDailyPracticeData(expandedTrackId);
    return {
      learning: Array.from(new Set([...current.learning, ...learningFactIds])),
      accuracy: Array.from(new Set([...current.accuracy, ...accuracyFactIds])),
      fluency: Array.from(new Set([...current.fluency, ...uniqueFluencyFactIds])),
    };
  }, [sessionData, expandedTrackId, isLoading]);

  // Persist merged progress only when API data is available
  useEffect(() => {
    if (!isLoading && expandedTrackId && sessionData?.availableActivities && dailyProgress) {
      try {
        updateDailyPracticeData(expandedTrackId, dailyProgress);
      } catch (e) {
        console.warn('Persist daily progress failed; continuing without storage.', e);
      }
    }
  }, [isLoading, expandedTrackId, sessionData?.availableActivities, dailyProgress]);

  // Calculate completion counts
  const progressCounts = useMemo(() => {
    // Return loading state if dailyProgress is not ready
    if (!dailyProgress) {
      return {
        totalLearning: 0,
        completedLearning: 0,
        totalAccuracy: 0,
        completedAccuracy: 0,
        totalFluency: 0,
        completedFluency: 0,
        isLoading: true
      };
    }
    
    if (!sessionData?.availableActivities) {
      return {
        totalLearning: dailyProgress.learning.length,
        completedLearning: 0,
        totalAccuracy: dailyProgress.accuracy.length,
        completedAccuracy: 0,
        totalFluency: dailyProgress.fluency.length,
        completedFluency: 0,
        isLoading: false
      };
    }

    // Get current learning fact IDs from session
    const currentLearningFactIds = sessionData.availableActivities.learn?.facts
      ?.filter((fact: any) => fact.progressStatus === 'learning' || !fact.progressStatus)
      ?.map((fact: any) => fact.factId) || [];
    
    // Learning completion: facts that were assigned but no longer available
    const completedLearning = dailyProgress.learning.filter((factId: string) => 
      !currentLearningFactIds.includes(factId)
    ).length;

    // Accuracy completion: check todayStats for completed facts
    let completedAccuracy = 0;
    let completedFluency = 0;
    if (expandedTrackId && allTracks.length > 0) {
      const currentTrackProgress = allTracks.find(track => track.trackId === expandedTrackId);
      if (currentTrackProgress?.facts) {
        // Get the stored practice date for comparison
        const storedPracticeDate = localStorage.getItem(`dailyPractice_${expandedTrackId}_date`);
        
        completedAccuracy = dailyProgress.accuracy.filter((factId: string) => {
          const factProgress = currentTrackProgress.facts[factId];
          
          // Relaxed completion criteria for accuracy facts:
          // 1. Check if fact status starts with 'fluency' (progressed beyond accuracy)
          if (factProgress?.status?.startsWith('fluency')) {
            return true;
          }
          
          // 2. Check if fact status is 'mastered'
          if (factProgress?.status === 'mastered') {
            return true;
          }
          
          // 3. Original criteria: todayStats with perfect performance
          if (!factProgress?.todayStats) return false;
          
          // Check accuracy contexts (accuracy1, accuracy2, etc.)
          for (const context in factProgress.todayStats) {
            if (context.startsWith('accuracy')) {
              const stats = factProgress.todayStats[context];
              // Only count if the context date matches the stored practice date
              if (stats.date === storedPracticeDate && 
                  stats.correct >= 3 && stats.attempts === stats.correct) {
                return true;
              }
            }
          }
          return false;
        }).length;

        // Fluency completion: check todayStats for completed facts
        completedFluency = dailyProgress.fluency.filter((factId: string) => {
          const factProgress = currentTrackProgress.facts[factId];
          
          // Relaxed completion criteria for fluency facts:
          // 1. Check if fact status is 'mastered'
          if (factProgress?.status === 'mastered') {
            return true;
          }
          
          // 2. Original criteria: todayStats with perfect performance
          if (!factProgress?.todayStats) return false;
          
          // Check fluency contexts (fluency1, fluency2, etc.)
          for (const context in factProgress.todayStats) {
            if (context.startsWith('fluency')) {
              const stats = factProgress.todayStats[context];
              // Only count if the context date matches the stored practice date
              if (stats.date === storedPracticeDate && 
                  stats.correct >= 3 && stats.attempts === stats.correct) {
                return true;
              }
            }
          }
          return false;
        }).length;
      }
    }

    return {
      totalLearning: dailyProgress.learning.length,
      completedLearning,
      totalAccuracy: dailyProgress.accuracy.length,
      completedAccuracy,
      totalFluency: dailyProgress.fluency.length,
      completedFluency,
      isLoading: false
    };
  }, [sessionData, dailyProgress, allTracks, expandedTrackId]);

  // Calculate and report completion status
  useEffect(() => {
    if (onCompletionStatusChange && !progressCounts.isLoading) {
      // Calculate if all progress items are completed
      const allLearningComplete = progressCounts.totalLearning === 0 || progressCounts.completedLearning >= progressCounts.totalLearning;
      const allAccuracyComplete = progressCounts.totalAccuracy === 0 || progressCounts.completedAccuracy >= progressCounts.totalAccuracy;
      const allFluencyComplete = progressCounts.totalFluency === 0 || progressCounts.completedFluency >= progressCounts.totalFluency;
      
      // Only consider it "all complete" if there's at least one activity assigned
      const hasAnyActivities = progressCounts.totalLearning > 0 || progressCounts.totalAccuracy > 0 || progressCounts.totalFluency > 0;
      const allComplete = hasAnyActivities && allLearningComplete && allAccuracyComplete && allFluencyComplete;
      
      onCompletionStatusChange(allComplete);
    }
  }, [progressCounts, onCompletionStatusChange]);

  // Show loading state when API is running or when progress counts indicate loading
  if (isLoading || progressCounts.isLoading) {
    return (
      <div aria-busy="true" aria-live="polite">
        <h3 className="text-lg font-semibold text-gray-700 mb-3">
          Today's Progress
        </h3>
        <div className="space-y-1">
          <div className="p-2 bg-gray-50 rounded-lg animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
          <div className="p-2 bg-gray-50 rounded-lg animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/3"></div>
          </div>
          <div className="p-2 bg-gray-50 rounded-lg animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-4/5 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-2/5"></div>
          </div>
        </div>
        <div className="text-xs text-gray-500 mt-2 flex items-center">
          <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
          Updating progress...
        </div>
      </div>
    );
  }

  if (!sessionData) {
    return (
      <div>
        <h3 className="text-lg font-semibold text-gray-700 mb-3">
          Today's Progress
        </h3>
        <div className="p-2 bg-gray-50 rounded-lg">
          <p className="text-gray-500 text-sm">No session data available</p>
        </div>
      </div>
    );
  }

  const progressItems: ProgressItem[] = [];

  // 1. Assessments completed (if any) - use backend data
  if (sessionData.dailyAssessmentCount > 0) {
    progressItems.push({
      text: `Completed ${sessionData.dailyAssessmentCount} assessment${sessionData.dailyAssessmentCount > 1 ? 's' : ''}`,
      completed: true
    });
  }

  // 2. Facts learned (using localStorage-based calculation)
  if (progressCounts.totalLearning > 0) {
    const learnCompleted = progressCounts.completedLearning >= progressCounts.totalLearning;
    progressItems.push({
      text: learnCompleted
        ? `Learned ${progressCounts.completedLearning} facts`
        : `Learned ${progressCounts.completedLearning}/${progressCounts.totalLearning} facts`,
      subtext: !learnCompleted 
        ? `${progressCounts.totalLearning - progressCounts.completedLearning} remaining`
        : undefined,
      completed: learnCompleted
    });
  }

  // 3. Accuracy practice (using localStorage-based calculation)
  if (progressCounts.totalAccuracy > 0) {
    const accuracyCompleted = progressCounts.completedAccuracy >= progressCounts.totalAccuracy;
    progressItems.push({
      text: accuracyCompleted
        ? `Practiced ${progressCounts.completedAccuracy} facts for accuracy`
        : `Practiced ${progressCounts.completedAccuracy}/${progressCounts.totalAccuracy} facts for accuracy`,
      subtext: !accuracyCompleted
        ? `${progressCounts.totalAccuracy - progressCounts.completedAccuracy} remaining`
        : undefined,
      completed: accuracyCompleted
    });
  }

  // 4. Fluency practice (using localStorage-based calculation)
  if (progressCounts.totalFluency > 0) {
    const fluencyCompleted = progressCounts.completedFluency >= progressCounts.totalFluency;
    progressItems.push({
      text: fluencyCompleted
        ? `Practiced ${progressCounts.completedFluency} facts for fluency`
        : `Practiced ${progressCounts.completedFluency}/${progressCounts.totalFluency} facts for fluency`,
      subtext: !fluencyCompleted
        ? `${progressCounts.totalFluency - progressCounts.completedFluency} remaining`
        : undefined,
      completed: fluencyCompleted
    });
  }


  // Don't render anything if there are no progress items
  if (progressItems.length === 0) {
    return null;
  }

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-700 mb-3">
        Today's Progress
      </h3>
      <div className="space-y-1">
        {progressItems.map((item, index) => (
          <div key={index} className={`p-2 rounded-lg transition-all duration-200 ${
            item.completed ? 'bg-green-50 hover:bg-green-100' : 'bg-gray-50 hover:bg-gray-100'
          }`}>
            <div className="flex items-start gap-2">
              <div className="mt-0.5">
                {item.completed ? (
                  <FiCheck className="w-4 h-4 text-green-600" />
                ) : (
                  <div className="w-2 h-2 rounded-full bg-gray-400 mt-1" />
                )}
              </div>
              <div className="flex-1">
                <p className={`text-sm ${item.completed ? "text-gray-700 font-medium" : "text-gray-600"}`}>
                  {item.text}
                </p>
                {item.subtext && (
                  <p className="text-xs text-gray-500 mt-0.5">{item.subtext}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TodaysProgress;